"use client";

import { useState } from "react";
import { useLocale } from "next-intl";
import { ChevronDown } from "lucide-react";
import type { Locale } from "@/i18n/routing";
import type { FaqEntry } from "@/content/faq";
import type { SectionsContent } from "@/content/sections";
import { hotlines, type LocaleKey } from "@/content/global";
import { PhoneCopyLink } from "@/components/layout/phone-copy-link";
import { t } from "@/lib/content";
import { cn } from "@/lib/cn";
import { Reveal } from "@/components/ui/reveal";

export function FaqAccordion({
  ids,
  faq,
  faqSection,
}: {
  ids: string[];
  faq: FaqEntry[];
  faqSection: SectionsContent["faqSection"];
}) {
  const locale = useLocale() as Locale;
  const line = hotlines[locale as LocaleKey] ?? hotlines.de;
  const items = ids
    .map((id) => faq.find((f) => f.id === id))
    .filter(Boolean) as typeof faq;
  const [open, setOpen] = useState<string | null>(null);

  return (
    <section
      id="faq"
      className="section relative border-y border-line-200 bg-canvas"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 65% 55% at 100% 0%, rgba(255, 0, 0, 0.05), transparent 65%)",
        }}
      />
      <div className="container relative">
        <header className="mx-auto max-w-2xl text-center">
          <p className="kicker">{t(faqSection.kicker, locale) as string}</p>
          <h2 className="mt-5 text-[1.75rem] leading-[1.08] tracking-[-0.02em] md:text-[2.25rem] lg:text-[2.5rem] xl:text-[3rem]">
            {t(faqSection.title, locale) as string}
          </h2>
          <p className="mx-auto mt-6 max-w-[58ch] text-base text-ink-700 md:text-lg leading-relaxed">
            {t(faqSection.body, locale) as string}
          </p>
        </header>

        <ul className="mx-auto mt-12 max-w-2xl divide-y divide-line-200 border-y border-line-200">
          {items.map((it, i) => {
            const isOpen = open === it.id;
            return (
              <li key={it.id}>
                <Reveal delay={i * 50}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : it.id)}
                  className="flex w-full items-center justify-between gap-6 py-5 text-left"
                >
                  <span className="font-display text-[15px] font-semibold leading-snug text-ink-900">
                    {t(it.q, locale) as string}
                  </span>
                  <ChevronDown
                    aria-hidden
                    className={cn(
                      "h-4 w-4 shrink-0 text-ink-500 transition-transform duration-200",
                      isOpen && "rotate-180 text-ink-900",
                    )}
                  />
                </button>
                <div
                  className={cn(
                    "grid transition-[grid-template-rows] duration-300 ease-out",
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="pb-5 pr-10 text-sm leading-relaxed text-ink-700">
                      {t(it.a, locale) as string}
                    </p>
                  </div>
                </div>
                </Reveal>
              </li>
            );
          })}
        </ul>

        <p className="mx-auto mt-10 max-w-xl text-center text-sm text-ink-500">
          {t(faqSection.contactPrompt, locale) as string}{" "}
          <PhoneCopyLink
            href={line.href}
            number={line.number}
            className="font-medium text-ink-900 underline-offset-2 hover:underline"
          >
            {line.number}
          </PhoneCopyLink>
          .
        </p>
      </div>
    </section>
  );
}
